import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { usePageTransition } from '../../utils/pageTransitions';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import StatsCard from '../administrative-dashboard/components/StatsCard';
import LeadManagementPanel from '../administrative-dashboard/components/LeadManagementPanel';
import ContentManagementPanel from '../administrative-dashboard/components/ContentManagementPanel';
import BookingManagementPanel from '../administrative-dashboard/components/BookingManagementPanel';
import TransactionHistoryPanel from '../administrative-dashboard/components/TransactionHistoryPanel';
import QuoteManagementPanel from '../administrative-dashboard/components/QuoteManagementPanel';
import ProjectManagementPanel from '../administrative-dashboard/components/ProjectManagementPanel';
import CRMPanel from '../administrative-dashboard/components/CRMPanel';
import { useAuth } from '../../contexts/AuthContext';
import { dashboardService } from '../../services/dashboardService';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const ProtectedAdminDashboard = () => {
  const { variants, transition } = usePageTransition();
  const { user, userProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');
  const [statsData, setStatsData] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [stats, recentActivities] = await Promise.all([
        dashboardService?.getStats(),
        dashboardService?.getActivities()
      ]);
      setStatsData(stats);
      setActivities(recentActivities);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/admin-login');
  };

  const quickActions = [
    {
      title: "Créer un Article de Blog",
      description: "Publier du nouveau contenu dans le centre de connaissances",
      icon: "PenTool",
      iconColor: "var(--color-primary)",
      onClick: () => setActiveSection('content')
    },
    {
      title: "Gérer les Prospects",
      description: "Examiner et répondre aux demandes des clients",
      icon: "UserCheck",
      iconColor: "var(--color-accent)",
      onClick: () => setActiveSection('leads')
    },
    {
      title: "Créer un Devis",
      description: "Générer un devis dynamique pour le client",
      icon: "FileText",
      iconColor: "var(--color-trust)",
      onClick: () => setActiveSection('quotes')
    },
    {
      title: "Voir les Projets",
      description: "Suivre la progression et les jalons des projets",
      icon: "Briefcase",
      iconColor: "var(--color-warning)",
      onClick: () => setActiveSection('projects')
    },
    {
      title: "Tableau de Bord CRM",
      description: "Gérer les relations clients et la satisfaction",
      icon: "Users",
      iconColor: "var(--color-primary)",
      onClick: () => setActiveSection('crm')
    },
    {
      title: "Gestion des Utilisateurs",
      description: "Inviter, modifier les rôles et gérer les accès utilisateurs",
      icon: "UserCog",
      iconColor: "var(--color-destructive)",
      onClick: () => navigate('/user-management')
    },
    {
      title: "Gestion personnalisée",
      description: "Gérer les services, propriétés et véhicules (CRUD)",
      icon: "Settings",
      iconColor: "var(--color-warning)",
      onClick: () => navigate('/admin-crud-management?tab=services')
    },
    {
      title: "Gestion des Stocks",
      description: "Suivre les niveaux de stock et gérer les fournisseurs",
      icon: "Package",
      iconColor: "var(--color-success)",
      onClick: () => navigate('/inventory-management-dashboard')
    },
    {
      title: "Planification d'Équipe",
      description: "Planifier les techniciens et chauffeurs avec assignation automatique",
      icon: "Calendar",
      iconColor: "var(--color-trust)",
      onClick: () => navigate('/team-planning-scheduling')
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <motion.div 
        className="container mx-auto px-4 pt-24 md:pt-28 pb-8"
        initial="initial"
        animate="animate"
        exit="exit"
        variants={variants}
        transition={transition}
      >
        {/* Header with User Info and Logout */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Shield" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">
                  Dashboard Administratif
                </h1>
              </div>
            </div>
            <p className="text-muted-foreground">
              Bienvenue, {userProfile?.full_name || user?.email} • Rôle: <span className="font-semibold capitalize">{userProfile?.role || 'staff'}</span>
            </p>
          </div>
          <Button 
            variant="outline" 
            iconName="LogOut" 
            iconPosition="left"
            onClick={handleLogout}
          >
            Sign Out
          </Button>
        </div>

        {/* Stats Cards */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading dashboard data...</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
              {statsData?.map((stat, index) => (
                <StatsCard key={index} {...stat} />
              ))}
            </div>

            {/* Quick Actions Grid */}
            {activeSection === 'overview' && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-foreground mb-6">Actions Rapides</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {quickActions?.map((action, index) => (
                    <button
                      key={index}
                      onClick={action?.onClick}
                      className="bg-card border border-border rounded-xl p-4 md:p-6 hover:shadow-lg transition-all duration-300 text-left group"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${action?.iconColor}15` }}>
                          <Icon name={action?.icon} size={24} color={action?.iconColor} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
                            {action?.title}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {action?.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Management Panels */}
            <div className="mb-8">
              {activeSection === 'content' && <ContentManagementPanel />}
              {activeSection === 'leads' && <LeadManagementPanel />}
              {activeSection === 'bookings' && <BookingManagementPanel />}
              {activeSection === 'transactions' && <TransactionHistoryPanel />}
              {activeSection === 'quotes' && <QuoteManagementPanel />}
              {activeSection === 'projects' && <ProjectManagementPanel />}
              {activeSection === 'crm' && <CRMPanel />}
            </div>
          </>
        )}
      </motion.div>
      <Footer />
    </div>
  );
};

export default ProtectedAdminDashboard;